/**
 * Customer card -> "Prekės" tab: what the customer bought in a period chosen
 * with the buttons above the table. Every period is a request of its own (the
 * server cuts the documents by day); searching and summing happen in the
 * browser. The figures come from sales invoices and returns in the ERP itself,
 * so they exist for customers with or without Finvalda data.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useRestorableState from "hooks/useRestorableState";
import { fetchCustomerProducts } from "services/customer-products.service";
import { CustomerProductsResponse } from "types/CustomerProducts";
import PeriodButtons from "./period-buttons";
import PeriodSummary from "./period-summary";
import ProductsTable from "./products-table";
import {
  DayRange,
  PeriodChoice,
  PresetId,
  productRowsOf,
  rangeOf,
  restoreChoice,
  totalsOf,
  WHOLE_HISTORY,
} from "./sales-by-period";

interface Props {
  companyId: string;
  customerId: string;
}

/** One question to the server. A new object for every change, so `===` tells whether an answer is for the current question. */
interface ProductsQuery {
  companyId: string;
  customerId: string;
  preset: PresetId;
  /** null = whole history. */
  range: DayRange | null;
  /** Grows with every "Bandyti dar kartą". */
  attempt: number;
}

type Answer = { status: "loaded"; data: CustomerProductsResponse } | { status: "failed" };

export default function CustomerProducts({ companyId, customerId }: Props) {
  // Kept so that Back from a product (or a reload) returns to the same period.
  const [savedChoice, setChoice] = useRestorableState<PeriodChoice>(
    "customer-products-period",
    WHOLE_HISTORY
  );
  const choice = useMemo(() => restoreChoice(savedChoice), [savedChoice]);
  // "Today" is read once per visit; the dates of a preset come from it and are never saved.
  const [today] = useState(() => new Date());
  const [attempt, setAttempt] = useState(0);

  const request: ProductsQuery = useMemo(
    () => ({ companyId, customerId, preset: choice.preset, range: rangeOf(choice, today), attempt }),
    [companyId, customerId, choice, today, attempt]
  );
  const [latest, setLatest] = useState<{ request: ProductsQuery; answer: Answer } | null>(null);

  useEffect(() => {
    // Goes false when the question changes or the tab closes, so a late answer is dropped.
    let isCurrent = true;
    fetchCustomerProducts(request.companyId, request.customerId, request.range)
      .then((data) => {
        if (isCurrent) setLatest({ request, answer: { status: "loaded", data } });
      })
      .catch(() => {
        if (isCurrent) setLatest({ request, answer: { status: "failed" } });
      });
    return () => {
      isCurrent = false;
    };
  }, [request]);

  // Another customer's answer is never shown; another period's is, dimmed, until the new one arrives.
  const isOfThisCustomer =
    latest && latest.request.companyId === companyId && latest.request.customerId === customerId;

  return (
    <div className="flex flex-col gap-4">
      <PeriodButtons choice={choice} onChange={setChoice} />
      {isOfThisCustomer ? (
        <Answered
          request={latest.request}
          answer={latest.answer}
          isReloading={latest.request !== request}
          onRetry={() => setAttempt((count) => count + 1)}
        />
      ) : (
        <ProductsSkeleton />
      )}
    </div>
  );
}

function Answered({
  request,
  answer,
  isReloading,
  onRetry,
}: {
  request: ProductsQuery;
  answer: Answer;
  /** A newer question is on its way; this answer is for the previous one. */
  isReloading: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  if (answer.status === "failed") {
    if (isReloading) return <ProductsSkeleton />;
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {t("customer_products_load_failed")}
        <button
          type="button"
          className="ml-3 rounded-md border border-red-300 bg-white px-3 py-1 text-red-700 hover:bg-red-100"
          onClick={onRetry}
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="customer-products-body"
      className={isReloading ? "opacity-50 transition-opacity" : "transition-opacity"}
      aria-busy={isReloading}
    >
      <ProductsOfPeriod request={request} data={answer.data} />
    </div>
  );
}

function ProductsOfPeriod({ request, data }: { request: ProductsQuery; data: CustomerProductsResponse }) {
  const { t } = useTranslation();
  const rows = useMemo(() => productRowsOf(data), [data]);
  const totals = totalsOf(rows);

  return (
    <div className="flex flex-col gap-4" data-testid="customer-products">
      <PeriodSummary
        preset={request.preset}
        range={request.range}
        totals={totals}
        unconvertedDocumentCount={data.unconvertedDocumentCount}
      />
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          {t(request.range ? "customer_products_empty_period" : "customer_products_empty_customer")}
        </p>
      ) : (
        <ProductsTable rows={rows} hasReturns={totals.returnedEur > 0} />
      )}
    </div>
  );
}

function ProductsSkeleton() {
  return (
    <div className="flex animate-pulse flex-col gap-4" data-testid="customer-products-skeleton">
      <div className="h-5 w-2/3 rounded bg-gray-100" />
      <div className="h-48 rounded-lg bg-gray-100" />
    </div>
  );
}
